import React, { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Icon, Link, Text } from '@Sage/hoi-poi-ui';

import EntityLink from 'components/EntityLink';
import { COMPANIES, CONTACTS } from 'constants/Entities';
import useEntityDetail from 'containers/components/EntityDetail/hooks/useEntityDetail';
import { getDateFromBackendISO } from 'utils/dates';
import { canViewEntity } from 'utils/entityPermissions';
import { stopEntityNavigation } from 'utils/entityNavigation';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { getCompanyDetailUrl, getContactDetailUrl } from 'utils/getUrl';

function formatValue(value) {
    return value != null && String(value).trim() ? String(value) : '—';
}

function formatDate(timestamp) {
    if (!timestamp) return '—';
    const date = getDateFromBackendISO(timestamp, 'LL');
    const time = getDateFromBackendISO(timestamp, 'LT');
    return getLiteralWithParameters('label_date_with_time', [date, time]);
}

const CELL_WRAPPER_CLASS = 'fm-campaign-details__cell-wrapper';

const RECIPIENT_ENTITY_MAP = {
    account: COMPANIES,
    accounts: COMPANIES,
    company: COMPANIES,
    companies: COMPANIES,
    contact: CONTACTS,
    contacts: CONTACTS,
};

function getRecipientEntity(entityType) {
    if (!entityType) return null;
    return RECIPIENT_ENTITY_MAP[String(entityType).toLowerCase()] ?? null;
}

function getRecipientDetailUrl(entity, entityId) {
    if (!entity || entityId == null) return undefined;
    if (entity === COMPANIES) return getCompanyDetailUrl(entityId);
    if (entity === CONTACTS) return getContactDetailUrl(entityId);
    return undefined;
}

// enableEntityLink is opt-in; temporarily off until PM decides expected click behavior.
const RecipientNameCell = memo(({ value, data, enableEntityLink = false }) => {
    const { openTab } = useEntityDetail();
    const isDetailOpen = useSelector((state) => state.entityDetail.isOpen);

    const entityType = data?.entity;
    const entityId = data?.entityId;
    const entity = useMemo(() => getRecipientEntity(entityType), [entityType]);
    const displayName = formatValue(value);
    const href = useMemo(
        () => (entity ? getRecipientDetailUrl(entity, entityId) : undefined),
        [entity, entityId],
    );

    const onClickEntity = useCallback(
        (e) => {
            if (!entity || entityId == null) return stopEntityNavigation(e);
            if (!canViewEntity(entity)) return stopEntityNavigation(e);

            if (isDetailOpen) {
                e.preventDefault();
                e.stopPropagation();
                openTab({
                    entity,
                    id: entityId,
                    hasCrudInDetail: true,
                });
            }
        },
        [entity, entityId, isDetailOpen, openTab],
    );

    const iconName =
        entity === COMPANIES ||
        ['account', 'accounts', 'company', 'companies'].includes(String(entityType).toLowerCase())
            ? 'accounts'
            : 'contacts';

    const nameContent =
        enableEntityLink && entity && entityId != null && href ? (
            <EntityLink
                entity={entity}
                href={href}
                onClick={isDetailOpen ? onClickEntity : undefined}
                linkProps={{
                    className: 'fm-campaign-details__recipient-name-link',
                    ...(isDetailOpen ? {} : { target: '_blank' }),
                }}
                textProps={{ medium: true, isTruncated: true }}
            >
                <Text medium isTruncated>
                    {displayName}
                </Text>
            </EntityLink>
        ) : (
            <Text medium isTruncated>
                {displayName}
            </Text>
        );

    return (
        <div className={CELL_WRAPPER_CLASS}>
            <span className="fm-campaign-details__cell-icon">
                <Icon name={iconName} size="smallMedium" />
            </span>
            {nameContent}
        </div>
    );
});

RecipientNameCell.displayName = 'RecipientNameCell';

const RecipientEmailCell = ({ value }) => (
    <div className={CELL_WRAPPER_CLASS}>
        <Text isTruncated>{formatValue(value)}</Text>
    </div>
);

const RecipientDateCell = ({ value }) => (
    <div className={CELL_WRAPPER_CLASS}>
        <Text>{formatDate(value)}</Text>
    </div>
);

const RecipientDescriptionCell = ({ value }) => (
    <div className={CELL_WRAPPER_CLASS}>
        <Text isTruncated>{formatValue(value)}</Text>
    </div>
);

const RecipientLinkCell = ({ data }) => {
    const url = data?.url || data?.link || data?.href || '';
    return (
        <div className={CELL_WRAPPER_CLASS}>
            {url ? (
                <Link
                    className="fm-campaign-details__event-link"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    isTruncated
                >
                    {url}
                </Link>
            ) : (
                <Text>—</Text>
            )}
        </div>
    );
};

/**
 * Get column definitions for SectionList based on event type.
 * @param {string} eventType
 * @param {{ enableEntityLink?: boolean }} [options]
 * @returns {Array<Object>} AG Grid column definitions
 */
export function getColumns(eventType, { enableEntityLink = false } = {}) {
    const nameColumn = {
        colId: 'name',
        headerName: getLiteral('label_name'),
        field: 'name',
        cellRenderer: 'RecipientNameCell',
        cellRendererParams: { enableEntityLink },
        flex: 1,
        minWidth: 180,
    };

    const COLUMN_DEFINITIONS = {
        default: [
            nameColumn,
            {
                colId: 'email',
                headerName: getLiteral('label_email_address'),
                field: 'email',
                cellRenderer: 'RecipientEmailCell',
                flex: 1,
                minWidth: 220,
            },
            {
                colId: 'timestamp',
                headerName: getLiteral('label_date'),
                field: 'timestamp',
                cellRenderer: 'RecipientDateCell',
                width: 180,
                minWidth: 160,
            },
        ],
        bounce: [
            nameColumn,
            {
                colId: 'email',
                headerName: getLiteral('label_email_address'),
                field: 'email',
                cellRenderer: 'RecipientEmailCell',
                flex: 1,
                minWidth: 220,
            },
            {
                colId: 'description',
                headerName: getLiteral('label_description'),
                field: 'bounceReason',
                cellRenderer: 'RecipientDescriptionCell',
                flex: 1,
                minWidth: 200,
            },
            {
                colId: 'timestamp',
                headerName: getLiteral('label_date'),
                field: 'timestamp',
                cellRenderer: 'RecipientDateCell',
                width: 180,
                minWidth: 160,
            },
        ],
    };
    return COLUMN_DEFINITIONS[eventType] ?? COLUMN_DEFINITIONS.default;
}

/**
 * Get cell renderer components for SectionList.
 * @returns {Object} Map of cell renderer names to components
 */
export function getComponents() {
    return {
        RecipientNameCell,
        RecipientEmailCell,
        RecipientDateCell,
        RecipientDescriptionCell,
        RecipientLinkCell,
    };
}
